import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import {
  Menu,
  Home as HomeIcon,
  Database,
  LogOut,
  MapPin,
  Map,
  Navigation,
  DollarSign,
  AlertCircle,
  Tag,
  Truck,
  UserCog,
  ScanLine,
  Search,
  FileText,
  Receipt,
  User,
  Users,
  Wrench,
  UserCheck,
  Building2,
  Shield,
  ChevronDown,
  ChevronUp,
  FileText as FileTextIcon,
  MessageSquare,
  Brush,
  Book,
  MessageCircle,
  Key,
  Upload as UploadIcon,
  CheckCircle,
  Package,
  RotateCcw,
  Warehouse,
  ShoppingCart,
  BarChart3,
  ArrowUp,
  List,
  CalendarDays,
  TrendingUp,
  Printer,
  ClipboardList,
  Edit,
  Flag,
  Droplet,
  ScrollText,
  ClipboardCheck,
  Gauge,
  AlertOctagon,
  History,
  LayoutDashboard,
  Car,
  Globe,
  Hash,
  Lock,
  RefreshCw,
  Calculator,
  BookOpen,
  ArrowDownCircle,
  ArrowUpCircle,
  Landmark,
  Clock,
  Scale,
  PieChart,
  Keyboard,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TabBar from './TabBar';
import Emirates from './Masters/Emirates';
import Areas from './Masters/Areas';
import Locations from './Masters/Locations';
import LocationBulkEdit from './Masters/LocationBulkEdit';
import Expenses from './Masters/Expenses';
import Reasons from './Masters/Reasons';
import Types from './Masters/Types';
import Vehicles from './Masters/Vehicles';
import Designations from './Masters/Designations';
import TrackByVoucher from './Tracking/TrackByVoucher';
import TrackByBill from './Tracking/TrackByBill';
import TrackByCustomer from './Tracking/TrackByCustomer';
import DriverTracking from './Tracking/DriverTracking';
import OrderDetails from './Orders/OrderDetails';
import OrderHistoryComparison from './Orders/OrderHistoryComparison';
import CreateOrder from './Orders/CreateOrder';
import OrderList from './Orders/OrderList';
import OrderCalendar from './Orders/OrderCalendar';
import HoldOrders from './Orders/HoldOrders';
import BatchPrint from './Orders/BatchPrint';
import ReverseCancel from './Orders/ReverseCancel';
import Employees from './Accounts/Employees';
import AddEmployee from './Accounts/AddEmployee';
import Clients from './Accounts/Clients';
import ClientPriceChart from './Accounts/ClientPriceChart';
import AddClient from './Accounts/AddClient';
import Customers from './Accounts/Customers';
import AddCustomer from './Accounts/AddCustomer';
import Agents from './Accounts/Agents';
import AddAgent from './Accounts/AddAgent';
import AccountUsers from './Accounts/Users';
import Dashboard from './Dashboard';
import VehicleDashboard from './Dashboards/VehicleDashboard';
import VehicleList from './VehicleManagement/VehicleList';
import AddEditVehicle from './VehicleManagement/AddEditVehicle';
import VehicleDetails from './VehicleManagement/VehicleDetails';
import VehicleMaintenance from './VehicleManagement/VehicleMaintenance';
import VehicleAssignments from './VehicleManagement/VehicleAssignments';
import VehicleDocuments from './VehicleManagement/VehicleDocuments';
import VehicleInsurance from './VehicleManagement/VehicleInsurance';
import VehicleFineRecords from './VehicleManagement/VehicleFineRecords';
import VehicleFuelLogs from './VehicleManagement/VehicleFuelLogs';
import VehiclePermits from './VehicleManagement/VehiclePermits';
import VehicleInspections from './VehicleManagement/VehicleInspections';
import VehicleExpenses from './VehicleManagement/VehicleExpenses';
import VehicleOdometerLogs from './VehicleManagement/VehicleOdometerLogs';
import VehicleAccidents from './VehicleManagement/VehicleAccidents';
import VehicleServiceHistory from './VehicleManagement/VehicleServiceHistory';
import AddMaintenance from './VehicleManagement/AddMaintenance';
import AddDocuments from './VehicleManagement/AddDocuments';
import AddInsurance from './VehicleManagement/AddInsurance';
import AddAssignment from './VehicleManagement/AddAssignment';
import AddFine from './VehicleManagement/AddFine';
import DraftEmails from './Messages/DraftEmails';
import DraftSMS from './Messages/DraftSMS';
import DeliveryNoteSMS from './Messages/DeliveryNoteSMS';
import CounterNos from './Counters/CounterNos';
import ChangeStoreRef from './Counters/ChangeStoreRef';
import AreaStoreNoLock from './Counters/AreaStoreNoLock';
import UpdateStoreRefNo from './Counters/UpdateStoreRefNo';
import DeliveryOrderFollowup from './Followup/DeliveryOrderFollowup';
import PickupOrderFollowup from './Followup/PickupOrderFollowup';
import DriverFollowup from './Followup/DriverFollowup';
import PaymentFollowup from './Followup/PaymentFollowup';
import ReturnFollowup from './Followup/ReturnFollowup';
import DriverExpenses from './Expenses/DriverExpenses';
import CompanyExpenses from './Expenses/CompanyExpenses';
import CommandPalette from './CommandPalette';
import DriverCommission from './Customization/DriverCommission';
import ClientAreaCharges from './Customization/ClientAreaCharges';
import VoucherBook from './Customization/VoucherBook';
import SMSConfiguration from './Customization/SMSConfiguration';
import WhatsappConfiguration from './Customization/WhatsappConfiguration';
import DocumentNumber from './Customization/DocumentNumber';
import Status from './Customization/Status';
import UserPageRole from './Customization/UserPageRole';
import PrintHeaderPage from './Customization/PrintHeader';
import OrderEntryTabSettings from './Customization/OrderEntryTabSettings';
import UserProfile from './User/UserProfile';
import CompanyProfile from './User/CompanyProfile';
import ChangePassword from './User/ChangePassword';
import LicenseInfo from './License/LicenseInfo';
import AccountSuspended from './License/AccountSuspended';
import ContractExpired from './License/ContractExpired';
import ContractExpiringWarning from './License/ContractExpiringWarning';
import LicenseExpiredWarning from './License/LicenseExpiredWarning';
import PaymentDueWarning from './License/PaymentDueWarning';
import {
  clearManagementCache,
  getRegisterCodeFromProfile,
  getPortalBlockReason,
  isAccountSuspended,
  isContractWarnDismissed,
  isLicenseWarnDismissed,
  isPaymentWarnDismissed,
  isSaRole,
  isAdminTabRole,
  canViewLicenseInfo,
  readCachedPortal,
  fetchCustomerPortal,
  cachePortal,
  setAccountSuspended,
  isPortalAccessBlocked,
} from '../services/managementService';
import {
  isAccountingEnabled,
  readStoredCompanyProfile,
  writeStoredCompanyProfile,
} from '../utils/companyProfileStorage';
import OrderUpload from './Uploads/OrderUpload';
import LocationUpload from './Uploads/LocationUpload';
import ClientUpload from './Uploads/ClientUpload';
import ShipmentApprovals from './Approvals/ShipmentApprovals';
import PaymentApprovals from './Approvals/PaymentApprovals';
import ReturnApprovals from './Approvals/ReturnApprovals';
import InScan from './Store/InScan';
import OutScan from './Store/OutScan';
import DeliveryScan from './Store/DeliveryScan';
import BulkInScan from './Store/BulkInScan';
import BulkOutScan from './Store/BulkOutScan';
import PickupScan from './Store/PickupScan';
import DeliveryAllocation from './Delivery/DeliveryAllocation';
import DeliveryNotes from './Delivery/DeliveryNotes';
import DeliveryStatistics from './Delivery/DeliveryStatistics';
import DeliveryExchange from './Delivery/DeliveryExchange';
import PushOrder from './Delivery/PushOrder';
import PickupAllocation from './Pickup/PickupAllocation';
import PickupNotes from './Pickup/PickupNotes';
import PickupStatistics from './Pickup/PickupStatistics';
import PickupExchange from './Pickup/PickupExchange';
import PushPickup from './Pickup/PushPickup';
import PickupStatus from './Pickup/PickupStatus';
import ClientPickupRequest from './Pickup/ClientPickupRequest';
import CustomerPickupRequest from './Pickup/CustomerPickupRequest';
import WarehouseDashboard from './Warehouse/WarehouseDashboard';
import WarehouseMaster from './Warehouse/WarehouseMaster';
import GoodsReceiving from './Warehouse/GoodsReceiving';
import InventoryRegister from './Warehouse/InventoryRegister';
import ShipmentSorting from './Warehouse/ShipmentSorting';
import PickList from './Warehouse/PickList';
import LoadingVerification from './Warehouse/LoadingVerification';
import DispatchManifest from './Warehouse/DispatchManifest';
import WarehouseTransferPage from './Warehouse/WarehouseTransferPage';
import ReturnsReceiving from './Warehouse/ReturnsReceiving';
import StockAdjustment from './Warehouse/StockAdjustment';
import WarehouseReports from './Warehouse/WarehouseReports';
import TodaysOrders from './Orders/TodaysOrders';
import NewShipment from './Shipments/NewShipment';
import ConvertShipment from './Shipments/ConvertShipment';
import ShipmentList from './Shipments/ShipmentList';
import CreatePayment from './Payments/CreatePayment';
import ClientPayments from './Payments/ClientPayments';
import PendingPayments from './Payments/PendingPayments';
import PaymentStatus from './Payments/PaymentStatus';
import CreateReturn from './Returns/CreateReturn';
import ClientReturns from './Returns/ClientReturns';
import PendingReturns from './Returns/PendingReturns';
import ReturnStatus from './Returns/ReturnStatus';
import ActivityTrackerReport from './Reports/ActivityTrackerReport';
import ReportPlaceholder from './Reports/ReportPlaceholder';
import OrderReport from './Reports/OrderReport';
import DeliveryReport from './Reports/DeliveryReport';
import ClientOrderReport from './Reports/ClientOrderReport';
import CustomerOrderReport from './Reports/CustomerOrderReport';
import EmployeeOrderReport from './Reports/EmployeeOrderReport';
import EmployeeTimeline from './Reports/EmployeeTimeline';
import DriverCommissionReport from './Reports/DriverCommissionReport';
import AdvanceOrderReport from './Reports/AdvanceOrderReport';
import DriverExpenseReport from './Reports/DriverExpenseReport';
import AgentOrderReport from './Reports/AgentOrderReport';
import PendingOrderReport from './Reports/PendingOrderReport';
import ShipmentExpenseReport from './Reports/ShipmentExpenseReport';
import VatReport from './Reports/VatReport';
import CompanyExpenseReport from './Reports/CompanyExpenseReport';
import IncomeExpenseReport from './Reports/IncomeExpenseReport';
import ClientPaymentReport from './Reports/ClientPaymentReport';
import ClientReturnReport from './Reports/ClientReturnReport';
import ShipmentReport from './Reports/ShipmentReport';
import InactiveClientsReport from './Reports/InactiveClientsReport';
import ClientPickupRequestReport from './Reports/ClientPickupRequestReport';
import CustomerPickupRequestReport from './Reports/CustomerPickupRequestReport';
import { REPORT_NAV_ITEMS, REPORT_SECTIONS } from './Reports/reportRouteMeta';
import { getNavT, getResolvedTabLabel } from '../locales/navigation';
import ReportCategoryHub from './Reports/ReportCategoryHub';
import AdminOrderEdit from './Admin/AdminOrderEdit';
import AdminOrderPaymentEdit from './Admin/AdminOrderPaymentEdit';
import AdminOrderStatusEdit from './Admin/AdminOrderStatusEdit';
import AdminOrderDateEdit from './Admin/AdminOrderDateEdit';
import AdminOrderLocationEdit from './Admin/AdminOrderLocationEdit';
import AdminOrderAdjustment from './Admin/AdminOrderAdjustment';
import AccountType from './Accounting/AccountType';
import ChartOfAccounts from './Accounting/ChartOfAccounts';
import AccountingVoucherList from './Accounting/AccountingVoucherList';
import AccountingVoucherForm from './Accounting/AccountingVoucherForm';
import PdcList from './Accounting/PdcList';
import GeneralLedgerReport from './Accounting/GeneralLedgerReport';
import FinancialStatementReport from './Accounting/FinancialStatementReport';
import { ACCOUNTING_VOUCHERS } from '../utils/accountingVouchers';
import { startUiActivityTracking } from '../services/uiActivityTracker';
import { initClientPublicIp } from '../services/clientPublicIp';
import MainService from '../services/MainService';
import Forbidden from './Forbidden';
import {
  clearPageAccessCache,
  ensurePageAccessLoaded,
  filterMenuItemsByAccess,
  getFirstAllowedPath,
  isPathAllowed,
  isPrivilegedRole,
  readUserInfo,
  resolvePageNameForPath,
} from '../services/pageAccess';

/** How often to call VerifyUser while the app shell is open */
const SESSION_VERIFY_INTERVAL_MS = 2 * 60 * 1000;
const SESSION_EXPIRED_STORAGE_KEY = 'ainsoft.sessionExpiredMessage';
const SESSION_EXPIRED_MESSAGE = 'Your User Session Expired Please Login Back';

function Home() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [homeOpen, setHomeOpen] = useState(false);
  const [mastersOpen, setMastersOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [vehicleManagementOpen, setVehicleManagementOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [uploadsOpen, setUploadsOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [shipmentsOpen, setShipmentsOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [returnsOpen, setReturnsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [companyProfileState, setCompanyProfileState] = useState(() => readStoredCompanyProfile());
  const [countersOpen, setCountersOpen] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [barcodeValue, setBarcodeValue] = useState('');
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [customizationMenuOpen, setCustomizationMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [allowedPages, setAllowedPages] = useState(null);
  const [accessReady, setAccessReady] = useState(false);
  const [suspended, setSuspended] = useState(() => isAccountSuspended());
  const [blockReason, setBlockReason] = useState(() => {
    const cached = readCachedPortal();
    return getPortalBlockReason(cached) || '';
  });
  const [licenseWarnPortal, setLicenseWarnPortal] = useState(null);
  const [contractWarnPortal, setContractWarnPortal] = useState(null);
  const [paymentWarnPortal, setPaymentWarnPortal] = useState(null);
  const sessionExpiredLogoutRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const tNav = useMemo(() => getNavT(language), [language]);
  const navLabel = useCallback((path) => getResolvedTabLabel(language, path), [language]);
  const pageAccessList = useMemo(() => {
    if (!accessReady) {
      const user = readUserInfo();
      return isPrivilegedRole(user.Role) ? null : [];
    }
    const user = readUserInfo();
    return isPrivilegedRole(user.Role) ? null : (allowedPages || []);
  }, [accessReady, allowedPages]);
  const filterMenu = useCallback(
    (items) => filterMenuItemsByAccess(items, pageAccessList),
    [pageAccessList]
  );
  const showAdminMenu = useMemo(() => isAdminTabRole(readUserInfo().Role), []);
  const showAccountingMenu = useMemo(
    () => isAccountingEnabled(companyProfileState),
    [companyProfileState]
  );

  useEffect(() => {
    initClientPublicIp();
    const stop = startUiActivityTracking();
    return () => {
      if (typeof stop === 'function') stop();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const applyProfile = (profile) => {
      if (!active || !profile) return;
      setCompanyProfileState(profile);
    };

    const refreshCompanyProfile = async () => {
      try {
        const fresh = await MainService.GetCompanyProfile();
        if (!active || !fresh) return;
        writeStoredCompanyProfile(fresh);
        setCompanyProfileState(fresh);
      } catch {
        applyProfile(readStoredCompanyProfile());
      }
    };

    refreshCompanyProfile();

    const onProfileUpdated = (event) => {
      applyProfile(event.detail || readStoredCompanyProfile());
    };
    window.addEventListener('ainsoft:company-profile-updated', onProfileUpdated);

    return () => {
      active = false;
      window.removeEventListener('ainsoft:company-profile-updated', onProfileUpdated);
    };
  }, []);

  // Re-validate management portal on shell mount (refresh / deep-link)
  useEffect(() => {
    let active = true;
    const user = readUserInfo();
    if (isSaRole(user.Role)) {
      setSuspended(false);
      setBlockReason('');
      setAccountSuspended(false);
      return undefined;
    }

    let profile = {};
    try {
      profile = JSON.parse(localStorage.getItem('companyProfile') || '{}');
    } catch {
      profile = {};
    }
    const code = getRegisterCodeFromProfile(profile);
    if (!code) {
      if (isAccountSuspended()) {
        setSuspended(true);
        setBlockReason(getPortalBlockReason(readCachedPortal()) || 'Suspended');
      }
      return undefined;
    }

    const applyPortalGate = (portal) => {
      if (!portal?.Found) return;
      if (isPortalAccessBlocked(portal)) {
        setAccountSuspended(true);
        setSuspended(true);
        setBlockReason(getPortalBlockReason(portal));
        return;
      }
      setAccountSuspended(false);
      setSuspended(false);
      setBlockReason('');
      // Soft reminders (app still usable). Priority: payment → contract → license
      if (portal.PaymentDueSoon && !isPaymentWarnDismissed()) {
        setPaymentWarnPortal(portal);
      } else if (portal.ContractExpiringSoon && !isContractWarnDismissed()) {
        setContractWarnPortal(portal);
      } else if (portal.HasExpiredLicense && !isLicenseWarnDismissed()) {
        setLicenseWarnPortal(portal);
      }
    };

    if (isAccountSuspended()) {
      setSuspended(true);
      setBlockReason(getPortalBlockReason(readCachedPortal()) || 'Suspended');
    }

    const cached = readCachedPortal();
    if (cached?.Found && isPortalAccessBlocked(cached)) {
      applyPortalGate(cached);
    }

    fetchCustomerPortal(code)
      .then((portal) => {
        if (!active) return;
        cachePortal(portal);
        applyPortalGate(portal);
      })
      .catch((err) => {
        console.warn('Management portal refresh failed:', err);
      });

    return () => {
      active = false;
    };
  }, []);

  // Soft warnings after login when portal was cached
  useEffect(() => {
    const user = readUserInfo();
    if (isSaRole(user.Role) || isAccountSuspended()) return;
    const cached = readCachedPortal();
    if (!cached?.Found) return;
    if (cached.PaymentDueSoon && !isPaymentWarnDismissed()) {
      setPaymentWarnPortal(cached);
    } else if (cached.ContractExpiringSoon && !isContractWarnDismissed()) {
      setContractWarnPortal(cached);
    } else if (cached.HasExpiredLicense && !isLicenseWarnDismissed()) {
      setLicenseWarnPortal(cached);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const user = readUserInfo();
    if (!user?.Id) {
      setAccessReady(true);
      return undefined;
    }

    if (isPrivilegedRole(user.Role)) {
      setAllowedPages(null);
      setAccessReady(true);
      return undefined;
    }

    ensurePageAccessLoaded(user)
      .then((pages) => {
        if (!active) return;
        setAllowedPages(pages || []);
        setAccessReady(true);
      })
      .catch(() => {
        if (!active) return;
        setAllowedPages([]);
        setAccessReady(true);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!accessReady) return undefined;
    const user = readUserInfo();
    if (isPrivilegedRole(user.Role)) return undefined;

    const path = location.pathname;
    if (path === '/home/forbidden') return undefined;

    const pages = allowedPages || [];
    const dashboardName = resolvePageNameForPath('/home');
    if ((path === '/home' || path === '/home/') && dashboardName && !pages.includes(dashboardName)) {
      const firstAllowed = getFirstAllowedPath(pages);
      if (firstAllowed && firstAllowed !== path) {
        navigate(firstAllowed, { replace: true });
      } else {
        navigate('/home/forbidden', { replace: true, state: { from: path } });
      }
      return undefined;
    }

    if (!isPathAllowed(path, pages)) {
      navigate('/home/forbidden', { replace: true, state: { from: path } });
      return undefined;
    }

    return undefined;
  }, [accessReady, allowedPages, location.pathname, navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setCustomizationMenuOpen(false);
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Refresh tab titles when language changes
  useEffect(() => {
    setTabs((prev) =>
      prev.map((tab) => ({ ...tab, label: getResolvedTabLabel(language, tab.path) }))
    );
  }, [language]);

  // Bootstrap tabs and handle route changes (add or activate tab)
  useEffect(() => {
    const currentPath = location.pathname;
    const label = getResolvedTabLabel(language, currentPath);

    setTabs((prev) => {
      let base = prev;
      if (prev.length === 0) {
        base = [
          {
            id: 'dashboard',
            path: '/home',
            label: getResolvedTabLabel(language, '/home'),
          },
        ];
      }

      const existingTab = base.find((tab) => tab.path === currentPath);
      if (existingTab) {
        setActiveTabId(existingTab.id);
        return base.length !== prev.length ? base : prev;
      }

      const newTab = {
        id: `tab-${Date.now()}`,
        path: currentPath,
        label,
      };
      setActiveTabId(newTab.id);
      return [...base, newTab];
    });
  }, [location.pathname, language]);

  // Handle tab click
  const handleTabClick = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setActiveTabId(tabId);
      navigate(tab.path);
    }
  };

  // Close a tab by path (used after save/delete from child pages)
  useEffect(() => {
    const onCloseTab = (event) => {
      const pathToClose = event.detail?.path;
      const nextPath = event.detail?.nextPath;
      if (!pathToClose) return;

      setTabs((prev) => {
        const tabIndex = prev.findIndex((t) => t.path === pathToClose);
        if (tabIndex === -1) return prev;
        const newTabs = prev.filter((t) => t.path !== pathToClose);
        if (newTabs.length === 0) {
          return [
            {
              id: 'dashboard',
              path: '/home',
              label: getResolvedTabLabel(language, '/home'),
            },
          ];
        }
        return newTabs;
      });

      navigate(nextPath || '/home');
    };

    window.addEventListener('ainsoft:close-tab', onCloseTab);
    return () => window.removeEventListener('ainsoft:close-tab', onCloseTab);
  }, [navigate, language]);

  // Handle tab close
  const handleTabClose = (tabId) => {
    const tabIndex = tabs.findIndex(t => t.id === tabId);
    if (tabIndex === -1) return;

    const newTabs = tabs.filter(t => t.id !== tabId);

    if (tabId === activeTabId) {
      if (newTabs.length > 0) {
        const nextTab = newTabs[Math.max(0, tabIndex - 1)] || newTabs[0];
        setActiveTabId(nextTab.id);
        navigate(nextTab.path);
        setTabs(newTabs);
        return;
      }
      setTabs([
        {
          id: 'dashboard',
          path: '/home',
          label: getResolvedTabLabel(language, '/home'),
        },
      ]);
      setActiveTabId('dashboard');
      navigate('/home');
      return;
    }

    setTabs(newTabs);
  };

  // Close all tabs except active
  const handleCloseOthers = () => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (activeTab) {
      setTabs([activeTab]);
    }
  };

  // Close all tabs to the right of active tab
  const handleCloseToRight = () => {
    const activeTabIndex = tabs.findIndex(t => t.id === activeTabId);
    if (activeTabIndex >= 0) {
      const newTabs = tabs.slice(0, activeTabIndex + 1);
      setTabs(newTabs);
    }
  };

  // Close all tabs to the left of active tab
  const handleCloseToLeft = () => {
    const activeTabIndex = tabs.findIndex(t => t.id === activeTabId);
    if (activeTabIndex > 0) {
      const newTabs = tabs.slice(activeTabIndex);
      setTabs(newTabs);
      // Active tab index changes, so update it
      const newActiveTab = newTabs[0];
      setActiveTabId(newActiveTab.id);
      navigate(newActiveTab.path);
    }
  };

  // Close all tabs and go to dashboard
  const handleCloseAll = () => {
    const dashboardTab = {
      id: 'dashboard',
      path: '/home',
      label: getResolvedTabLabel(language, '/home'),
    };
    setTabs([dashboardTab]);
    setActiveTabId('dashboard');
    navigate('/home');
  };

  // Check authentication on mount
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      navigate('/login');
    }
  }, [navigate]);

  // Periodic session validity (blocked user / invalid token)
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return undefined;

    const isExplicitlyInvalid = (r) => r === false || r === 'false';

    const verifySession = async () => {
      if (sessionExpiredLogoutRef.current) return;
      if (!localStorage.getItem('authToken')) return;
      try {
        const result = await MainService.VerifyUser();
        if (isExplicitlyInvalid(result)) {
          sessionExpiredLogoutRef.current = true;
          try {
            sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, SESSION_EXPIRED_MESSAGE);
          } catch {
            /* ignore quota / private mode */
          }
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
          localStorage.removeItem('companyProfile');
          clearPageAccessCache();
          clearManagementCache();
          navigate('/login', { replace: true });
        }
      } catch (e) {
        console.warn('VerifyUser check failed:', e);
      }
    };

    void verifySession();
    const intervalId = window.setInterval(verifySession, SESSION_VERIFY_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [navigate]);

  // Keyboard shortcut for command palette (Ctrl+K or Cmd+K)
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleQuickTrack = () => {
    const value = barcodeValue.trim();
    if (!value) return;
    navigate(`/home/tracking/voucher?voucher=${encodeURIComponent(value)}`);
    setBarcodeValue('');
  };

  const handleQuickTrackKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuickTrack();
    }
  };

  const performLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('companyProfile');
    clearPageAccessCache();
    clearManagementCache();
    setAllowedPages(null);
    setAccessReady(false);
    setUserMenuOpen(false);
    setLogoutConfirmOpen(false);
    navigate('/login');
  };

  const openLogoutConfirm = () => {
    setCustomizationMenuOpen(false);
    setUserMenuOpen(false);
    setLogoutConfirmOpen(true);
  };

  const homeMenuItems = [
    { path: '/home', icon: LayoutDashboard },
    { path: '/home/dashboards/vehicle', icon: Car },
  ];

  const mastersMenuItems = [
    { path: '/home/masters/emirates', icon: MapPin },
    { path: '/home/masters/areas', icon: Map },
    { path: '/home/masters/locations', icon: Navigation },
    { path: '/home/masters/expenses', icon: DollarSign },
    { path: '/home/masters/reasons', icon: AlertCircle },
    { path: '/home/masters/types', icon: Tag },
    { path: '/home/masters/vehicles', icon: Truck },
    { path: '/home/masters/designations', icon: UserCog },
  ];

  const trackingMenuItems = [
    { path: '/home/tracking/voucher', icon: FileText },
    { path: '/home/tracking/bill', icon: Receipt },
    { path: '/home/tracking/customer', icon: User },
    { path: '/home/tracking/driver', icon: MapPin },
    { path: '/home/tracking/activity-tracker', icon: BarChart3 },
  ];

  const reportIconByName = {
    ShoppingCart,
    ClipboardList,
    Truck,
    Package,
    Building2,
    Users,
    UserCheck,
    UserCog,
    Receipt,
    DollarSign,
    RotateCcw,
    TrendingUp,
  };
  const reportsMenuItems = REPORT_NAV_ITEMS.map((item) => ({
    path: `/home/reports/${item.slug}`,
    icon: reportIconByName[item.iconName] || BarChart3,
  }));

  const adminMenuItems = [
    { path: '/home/admin/order-edit', icon: Edit },
    { path: '/home/admin/order-payment-edit', icon: DollarSign },
    { path: '/home/admin/order-status-edit', icon: Flag },
    { path: '/home/admin/order-date-edit', icon: CalendarDays },
    { path: '/home/admin/order-location-edit', icon: MapPin },
    { path: '/home/admin/order-adjustment', icon: ClipboardList, saOnly: true },
  ];

  const voucherIconBySlug = {
    cpv: ArrowDownCircle,
    crv: ArrowUpCircle,
    fpv: Landmark,
    frv: Landmark,
    'cheque-pv': Receipt,
    'cheque-rv': Receipt,
  };

  const accountingMenuItems = [
    { path: '/home/accounting/account-types', icon: Tag },
    { path: '/home/accounting/chart-of-accounts', icon: BookOpen },
    ...ACCOUNTING_VOUCHERS.map((v) => ({
      path: `/home/accounting/vouchers/${v.slug}`,
      icon: voucherIconBySlug[v.slug] || FileText,
    })),
    { path: '/home/accounting/pdc/pending', icon: Clock },
    { path: '/home/accounting/pdc/processed', icon: Clock },
    { path: '/home/accounting/general-ledger', icon: ScrollText },
    { path: '/home/accounting/trial-balance', icon: Scale },
    { path: '/home/accounting/balance-sheet', icon: PieChart },
    { path: '/home/accounting/profit-loss', icon: TrendingUp },
  ];

  const countersMenuItems = [
    { path: '/home/counters/counter-nos', icon: Hash },
    { path: '/home/counters/change-store-ref', icon: RefreshCw },
    { path: '/home/counters/area-store-no-lock', icon: Lock },
    { path: '/home/counters/update-store-ref-no', icon: Gauge },
  ];

  const accountsMenuItems = [
    { path: '/home/accounts/employees', icon: Users },
    { path: '/home/accounts/clients', icon: Building2 },
    { path: '/home/accounts/customers', icon: User },
    { path: '/home/accounts/agents', icon: UserCheck },
    { path: '/home/accounts/users', icon: UserCog },
  ];

  const vehicleManagementMenuItems = [
    { path: '/home/vehicle-management/vehicle-list', icon: Truck },
    { path: '/home/vehicle-management/assignments', icon: UserCheck },
    { path: '/home/vehicle-management/maintenance', icon: Wrench },
    { path: '/home/vehicle-management/fuel-logs', icon: Droplet },
    { path: '/home/vehicle-management/documents', icon: FileText },
    { path: '/home/vehicle-management/insurance', icon: Shield },
    { path: '/home/vehicle-management/permits', icon: ScrollText },
    { path: '/home/vehicle-management/inspections', icon: ClipboardCheck },
    { path: '/home/vehicle-management/vehicle-expenses', icon: Receipt },
    { path: '/home/vehicle-management/odometer-logs', icon: Gauge },
    { path: '/home/vehicle-management/accidents', icon: AlertOctagon },
    { path: '/home/vehicle-management/service-history', icon: History },
    { path: '/home/vehicle-management/fine-records', icon: FileTextIcon },
  ];

  const uploadsMenuItems = [
    { path: '/home/uploads/order-upload', icon: UploadIcon },
    { path: '/home/uploads/location-upload', icon: MapPin },
    { path: '/home/uploads/client-upload', icon: Building2 },
  ];

  const messagesMenuItems = [
    { path: '/home/messages/draft-emails', icon: MessageSquare },
    { path: '/home/messages/draft-sms', icon: MessageSquare },
    { path: '/home/messages/delivery-note-sms', icon: FileText },
  ];

  const approvalsMenuItems = [
    { path: '/home/approvals/shipment-approvals', icon: Package },
    { path: '/home/approvals/payment-approvals', icon: DollarSign },
    { path: '/home/approvals/return-approvals', icon: RotateCcw },
  ];

  const storeMenuItems = [
    { path: '/home/store/in-scan', icon: ScanLine },
    { path: '/home/store/out-scan', icon: ScanLine },
    { path: '/home/store/delivery-scan', icon: Package },
    { path: '/home/store/bulk-in-scan', icon: ScanLine },
    { path: '/home/store/bulk-out-scan', icon: ScanLine },
    { path: '/home/store/pickup-scan', icon: ScanLine },
  ];

  const ordersMenuItems = [
    { path: '/home/orders/create-order', icon: ShoppingCart },
    { path: '/home/orders/order-list', icon: List },
    { path: '/home/orders/order-calendar', icon: CalendarDays },
    { path: '/home/orders/todays-orders', icon: Package },
    { path: '/home/orders/hold-orders', icon: AlertCircle },
    { path: '/home/orders/reverse-cancel', icon: RotateCcw },
    { path: '/home/orders/batch-print', icon: Printer },
  ];

  const followupMenuItems = [
    { path: '/home/followup/delivery-order', icon: Truck },
    { path: '/home/followup/pickup-order', icon: Package },
    { path: '/home/followup/driver', icon: UserCheck },
    { path: '/home/followup/payment', icon: DollarSign },
    { path: '/home/followup/return', icon: RotateCcw },
  ];

  const expensesMenuItems = [
    { path: '/home/expenses/driver-expense', icon: Truck },
    { path: '/home/expenses/company-expense', icon: Building2 },
  ];

  const pickupMenuItems = [
    { path: '/home/pickup/allocation', icon: Truck },
    { path: '/home/pickup/notes', icon: FileText },
    { path: '/home/pickup/statistics', icon: BarChart3 },
    { path: '/home/pickup/exchange', icon: RotateCcw },
    { path: '/home/pickup/push-pickup', icon: ArrowUp },
    { path: '/home/pickup/status', icon: CheckCircle },
    { path: '/home/pickup/client-pickup-request', icon: Building2 },
    { path: '/home/pickup/customer-pickup-request', icon: Users },
  ];

  const warehouseMenuItems = [
    { path: '/home/warehouse/dashboard', icon: Warehouse },
    { path: '/home/warehouse/master', icon: Building2 },
    { path: '/home/warehouse/goods-receiving', icon: Package },
    { path: '/home/warehouse/inventory-register', icon: List },
    { path: '/home/warehouse/shipment-sorting', icon: Tag },
    { path: '/home/warehouse/pick-list', icon: CheckCircle },
    { path: '/home/warehouse/loading-verification', icon: ScanLine },
    { path: '/home/warehouse/dispatch-manifest', icon: Truck },
    { path: '/home/warehouse/transfer', icon: ArrowUp },
    { path: '/home/warehouse/returns-receiving', icon: RotateCcw },
    { path: '/home/warehouse/stock-adjustment', icon: Edit },
    { path: '/home/warehouse/reports', icon: BarChart3 },
  ];

  const deliveryMenuItems = [
    { path: '/home/delivery/allocation', icon: Truck },
    { path: '/home/delivery/notes', icon: FileText },
    { path: '/home/delivery/statistics', icon: BarChart3 },
    { path: '/home/delivery/exchange', icon: RotateCcw },
    { path: '/home/delivery/push-order', icon: ArrowUp },
  ];

  const shipmentsMenuItems = [
    { path: '/home/shipments/new-shipment', icon: Package },
    { path: '/home/shipments/shipment-list', icon: Package },
  ];

  const paymentsMenuItems = [
    { path: '/home/payments/create-payment', icon: DollarSign },
    { path: '/home/payments/client-payments', icon: DollarSign },
    { path: '/home/payments/pending-payments', icon: DollarSign },
  ];

  const returnsMenuItems = [
    { path: '/home/returns/create-return', icon: RotateCcw },
    { path: '/home/returns/client-returns', icon: Building2 },
    { path: '/home/returns/pending-returns', icon: AlertCircle },
  ];

  const customizationMenuItems = [
    { path: '/home/customization/driver-commission', icon: DollarSign },
    { path: '/home/customization/client-area-charges', icon: MapPin },
    { path: '/home/customization/voucher-book', icon: Book },
    { path: '/home/customization/sms-configuration', icon: MessageSquare },
    { path: '/home/customization/whatsapp-configuration', icon: MessageCircle },
    { path: '/home/customization/document-number', icon: FileText },
    { path: '/home/customization/status', icon: Flag },
    { path: '/home/customization/user-page-role', icon: Shield },
    { path: '/home/customization/print-header', icon: Printer },
    { path: '/home/customization/order-entry-tab', icon: Keyboard },
  ];
  const filteredHomeMenuItems = filterMenu(homeMenuItems);
  const filteredMastersMenuItems = filterMenu(mastersMenuItems);
  const filteredAccountsMenuItems = filterMenu(accountsMenuItems);
  const filteredOrdersMenuItems = filterMenu(ordersMenuItems);
  const filteredFollowupMenuItems = filterMenu(followupMenuItems);
  const filteredExpensesMenuItems = filterMenu(expensesMenuItems);
  const filteredStoreMenuItems = filterMenu(storeMenuItems);
  const filteredUploadsMenuItems = filterMenu(uploadsMenuItems);
  const filteredPickupMenuItems = filterMenu(pickupMenuItems);
  const filteredWarehouseMenuItems = filterMenu(warehouseMenuItems);
  const filteredDeliveryMenuItems = filterMenu(deliveryMenuItems);
  const filteredShipmentsMenuItems = filterMenu(shipmentsMenuItems);
  const filteredPaymentsMenuItems = filterMenu(paymentsMenuItems);
  const filteredReturnsMenuItems = filterMenu(returnsMenuItems);
  const filteredApprovalsMenuItems = filterMenu(approvalsMenuItems);
  const filteredTrackingMenuItems = filterMenu(trackingMenuItems);
  const filteredMessagesMenuItems = filterMenu(messagesMenuItems);
  const filteredVehicleManagementMenuItems = filterMenu(vehicleManagementMenuItems);
  const filteredReportsMenuItems = filterMenu(reportsMenuItems);
  const filteredAccountingMenuItems = filterMenu(accountingMenuItems);
  const filteredCountersMenuItems = filterMenu(countersMenuItems);
  const filteredCustomizationMenuItems = filterMenu(customizationMenuItems);

  // Navigation handler that manages tabs
  const handleNavigation = (path) => {
    navigate(path);
  };

  if (suspended && !isSaRole(readUserInfo().Role)) {
    if (blockReason === 'ContractExpired') {
      return <ContractExpired />;
    }
    return <AccountSuspended />;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`${sidebarOpen ? 'w-64' : 'w-20'} sidebar-animated-bg flex flex-col border-r border-white/10 bg-sidebar-gradient transition-all duration-300`}
      >
        {/* Logo/Brand */}
        <div className="h-16 flex items-center border-b border-gray-300 border-opacity-30 px-4">
          {sidebarOpen ? (
            <div className="flex items-center gap-3 w-full">
              <div className="flex-shrink-0">
                <div className="bg-white bg-opacity-20 rounded p-1.5">
                  <img 
                    src="/images/logo.png" 
                    alt="Ainsoft Logo" 
                    className="w-6 h-6 brightness-0 invert"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-lg font-bold text-white leading-snug font-sans tracking-tight">{tNav('brand.title')}</h1>
                <p className="text-sm font-normal text-white opacity-75 leading-snug font-sans">{tNav('brand.subtitle')}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <div className="bg-white bg-opacity-20 rounded p-1.5">
                <img 
                  src="/images/logo.png" 
                  alt="Ainsoft Logo" 
                  className="w-6 h-6 brightness-0 invert"
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 sidebar-nav-scroll">
          <div className="px-2 space-y-1">

            {/* Home: main dashboard + dashboards (vehicle) */}
            <div className={filteredHomeMenuItems.length ? '' : 'hidden'}>
              <button
                type="button"
                onClick={() => setHomeOpen(!homeOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <HomeIcon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.home')}</span>}
                </div>
                {sidebarOpen &&
                  (homeOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  ))}
              </button>
              {homeOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredHomeMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Masters Menu */}
            <div className={filteredMastersMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setMastersOpen(!mastersOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Database className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.masters')}</span>}
                </div>
                {sidebarOpen && (
                  mastersOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {mastersOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredMastersMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Accounts Menu */}
            <div className={filteredAccountsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setAccountsOpen(!accountsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Users className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.accounts')}</span>}
                </div>
                {sidebarOpen && (
                  accountsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {accountsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredAccountsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Counters Menu */}
            <div className={filteredCountersMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setCountersOpen(!countersOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Gauge className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.counters')}</span>}
                </div>
                {sidebarOpen && (
                  countersOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              {countersOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredCountersMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-left"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Orders Menu */}
            <div className={filteredOrdersMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setOrdersOpen(!ordersOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <ShoppingCart className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.orders')}</span>}
                </div>
                {sidebarOpen && (
                  ordersOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {ordersOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredOrdersMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Store Menu */}
            <div className={filteredStoreMenuItems.length ? '' : 'hidden'}>
              <button
                type="button"
                onClick={() => setStoreOpen(!storeOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Warehouse className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.store')}</span>}
                </div>
                {sidebarOpen &&
                  (storeOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  ))}
              </button>

              {storeOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredStoreMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Uploads Menu */}
            <div className={filteredUploadsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setUploadsOpen(!uploadsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <UploadIcon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.uploads')}</span>}
                </div>
                {sidebarOpen && (
                  uploadsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {uploadsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredUploadsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pickup Menu */}
            <div className={filteredPickupMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setPickupOpen(!pickupOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Package className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.pickup')}</span>}
                </div>
                {sidebarOpen && (
                  pickupOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>

              {pickupOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredPickupMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Delivery Menu */}
            <div className={filteredDeliveryMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setDeliveryOpen(!deliveryOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Truck className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.delivery')}</span>}
                </div>
                {sidebarOpen && (
                  deliveryOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {deliveryOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredDeliveryMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Followup Menu */}
            <div className={filteredFollowupMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setFollowupOpen(!followupOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <ClipboardList className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.followup')}</span>}
                </div>
                {sidebarOpen && (
                  followupOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>

              {followupOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredFollowupMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-left"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tracking Menu */}
            <div className={filteredTrackingMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setTrackingOpen(!trackingOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Search className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.tracking')}</span>}
                </div>
                {sidebarOpen && (
                  trackingOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {trackingOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredTrackingMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Shipments Menu */}
            <div className={filteredShipmentsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setShipmentsOpen(!shipmentsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Package className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.shipments')}</span>}
                </div>
                {sidebarOpen && (
                  shipmentsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {shipmentsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredShipmentsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payments Menu */}
            <div className={filteredPaymentsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setPaymentsOpen(!paymentsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.payments')}</span>}
                </div>
                {sidebarOpen && (
                  paymentsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {paymentsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredPaymentsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Returns Menu */}
            <div className={filteredReturnsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setReturnsOpen(!returnsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <RotateCcw className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.returns')}</span>}
                </div>
                {sidebarOpen && (
                  returnsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>

              {returnsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredReturnsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Approvals Menu */}
            <div className={filteredApprovalsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setApprovalsOpen(!approvalsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.approvals')}</span>}
                </div>
                {sidebarOpen && (
                  approvalsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {approvalsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredApprovalsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Messages Menu */}
            <div className={filteredMessagesMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setMessagesOpen(!messagesOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <MessageSquare className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.messages')}</span>}
                </div>
                {sidebarOpen && (
                  messagesOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {messagesOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredMessagesMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Expenses Menu */}
            <div className={filteredExpensesMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setExpensesOpen(!expensesOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Receipt className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.expenses')}</span>}
                </div>
                {sidebarOpen && (
                  expensesOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>

              {expensesOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredExpensesMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-left"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Vehicle management */}
            <div className={filteredVehicleManagementMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setVehicleManagementOpen(!vehicleManagementOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Truck className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.vehicleManagement')}</span>}
                </div>
                {sidebarOpen && (
                  vehicleManagementOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              
              {vehicleManagementOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredVehicleManagementMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Warehouse Menu (after Vehicle) */}
            <div className={filteredWarehouseMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setWarehouseOpen(!warehouseOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <Warehouse className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.warehouse')}</span>}
                </div>
                {sidebarOpen && (
                  warehouseOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>

              {warehouseOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredWarehouseMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Accounting Menu */}
            {showAccountingMenu && filteredAccountingMenuItems.length > 0 && (
              <div>
                <button
                  onClick={() => setAccountingOpen(!accountingOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  <div className="flex items-center">
                    <Calculator className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && <span className="ml-3">{tNav('sections.accounting')}</span>}
                  </div>
                  {sidebarOpen && (
                    accountingOpen ? (
                      <ChevronUp className="w-4 h-4 text-white" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-white" />
                    )
                  )}
                </button>
                {accountingOpen && (
                  <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                    {filteredAccountingMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-left"
                          title={!sidebarOpen ? navLabel(item.path) : ''}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Reports Menu (after Vehicle) */}
            <div className={filteredReportsMenuItems.length ? '' : 'hidden'}>
              <button
                onClick={() => setReportsOpen(!reportsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <div className="flex items-center">
                  <BarChart3 className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="ml-3">{tNav('sections.reports')}</span>}
                </div>
                {sidebarOpen && (
                  reportsOpen ? (
                    <ChevronUp className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white" />
                  )
                )}
              </button>
              {reportsOpen && (
                <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                  {filteredReportsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-left"
                        title={!sidebarOpen ? navLabel(item.path) : ''}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Admin Menu (SA / CA only, after Reports) */}
            {showAdminMenu && (
              <div>
                <button
                  onClick={() => setAdminOpen(!adminOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && <span className="ml-3">{tNav('sections.admin')}</span>}
                  </div>
                  {sidebarOpen && (
                    adminOpen ? (
                      <ChevronUp className="w-4 h-4 text-white" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-white" />
                    )
                  )}
                </button>
                {adminOpen && (
                  <div className={`${sidebarOpen ? 'ml-8' : ''} mt-1 space-y-1`}>
                    {adminMenuItems
                      .filter((item) => !item.saOnly || isSaRole(readUserInfo().Role))
                      .map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className="w-full flex items-center px-3 py-2 text-sm text-white text-opacity-90 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors text-left"
                          title={!sidebarOpen ? navLabel(item.path) : ''}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {sidebarOpen && <span className="ml-3">{navLabel(item.path)}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Logout */}
        <div className="border-t border-gray-300 border-opacity-30 p-2">
          <button
            type="button"
            onClick={openLogoutConfirm}
            className="w-full flex items-center px-3 py-2 text-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="ml-3">{tNav('logout')}</span>}
          </button>
        </div>
      </div>

      {/* Main Content — min-h-0 so flex child can shrink and trackpad/wheel scroll works */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top Menu */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            <div className="relative w-64">
              <input
                type="text"
                value={barcodeValue}
                onChange={(e) => setBarcodeValue(e.target.value)}
                onKeyDown={handleQuickTrackKeyDown}
                placeholder="Quick Track"
                aria-label="Quick Track"
                className={`w-full py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary text-sm bg-transparent ${
                  barcodeValue ? 'pl-4 pr-4' : 'pl-10 pr-4'
                }`}
              />
              {!barcodeValue && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                  <ScanLine className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>

            {/* Command Palette Trigger */}
            <button
              onClick={() => setCommandPaletteOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-600"
              title="Open command palette (Ctrl+K / Cmd+K)"
            >
              <Search className="w-4 h-4" />
              <span className="hidden md:inline">Commands</span>
              <kbd className="hidden md:inline px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">
                ⌘K
              </kbd>
            </button>
          </div>

          <div className="flex items-center space-x-3">
            {/* Language */}
            <div
              className="flex items-center gap-1.5 shrink-0"
              role="group"
              aria-label={language === 'ar' ? 'اللغة' : 'Language'}
            >
              <Globe className="w-4 h-4 text-gray-500 hidden sm:block" aria-hidden />
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                    language === 'en'
                      ? 'bg-[#46955A] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('ar')}
                  className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                    language === 'ar'
                      ? 'bg-[#46955A] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  عربي
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/home/orders/order-calendar')}
              className="flex items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-primary"
              title={navLabel('/home/orders/order-calendar')}
              aria-label={navLabel('/home/orders/order-calendar')}
            >
              <CalendarDays className="h-5 w-5" />
            </button>

            {/* Customization Dropdown */}
            {(pageAccessList === null || filteredCustomizationMenuItems.length > 0) && (
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setCustomizationMenuOpen(!customizationMenuOpen);
                  setUserMenuOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                title={tNav('sections.customization')}
              >
                <Brush className="w-5 h-5" />
                {customizationMenuOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {customizationMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100] dropdown-container">
                  {filteredCustomizationMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setCustomizationMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                      >
                        <Icon className="w-4 h-4" />
                        <span>{navLabel(item.path)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* License Info — Admin / SA / CA / role 0 only */}
            {canViewLicenseInfo(readUserInfo().Role) && (
              <button
                type="button"
                onClick={() => {
                  setCustomizationMenuOpen(false);
                  setUserMenuOpen(false);
                  navigate('/home/license-info');
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                title={tNav('user.licenseInfo')}
              >
                <Key className="w-5 h-5" />
                <span className="hidden lg:inline text-sm font-medium">{tNav('user.licenseInfo')}</span>
              </button>
            )}

            {/* User Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setUserMenuOpen(!userMenuOpen);
                  setCustomizationMenuOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                title={tNav('user.menuTitle')}
              >
                <User className="w-5 h-5" />
                {userMenuOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100] dropdown-container">
                  <button
                    onClick={() => {
                      navigate('/home/user/profile');
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                  >
                    <User className="w-4 h-4" />
                    <span>{tNav('user.profile')}</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/home/user/company-profile');
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                  >
                    <Building2 className="w-4 h-4" />
                    <span>{tNav('user.companyProfile')}</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/home/user/change-password');
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                  >
                    <Key className="w-4 h-4" />
                    <span>{tNav('user.changePassword')}</span>
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    type="button"
                    onClick={openLogoutConfirm}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-red-50 text-red-600"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{tNav('logout')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Logout Icon */}
            <button
              type="button"
              onClick={openLogoutConfirm}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              title={tNav('logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Tab Bar */}
        {tabs.length > 0 && (
          <div className="relative z-50">
            <TabBar
              tabs={tabs}
              activeTabId={activeTabId}
              onTabClick={handleTabClick}
              onTabClose={handleTabClose}
              onCloseOthers={handleCloseOthers}
              onCloseToRight={handleCloseToRight}
              onCloseToLeft={handleCloseToLeft}
              onCloseAll={handleCloseAll}
            />
          </div>
        )}

        {/* Command Palette */}
        <CommandPalette 
          isOpen={commandPaletteOpen} 
          onClose={() => setCommandPaletteOpen(false)} 
        />

        {/* Page Content — min-h-0 required for overflow scroll inside flex column */}
        <main className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-6">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="forbidden" element={<Forbidden />} />
            <Route path="dashboards/vehicle" element={<VehicleDashboard />} />
            <Route path="masters/emirates" element={<Emirates />} />
            <Route path="masters/areas" element={<Areas />} />
            <Route path="masters/locations" element={<Locations />} />
            <Route path="masters/location-edit" element={<LocationBulkEdit />} />
            <Route path="masters/expenses" element={<Expenses />} />
            <Route path="masters/reasons" element={<Reasons />} />
            <Route path="masters/types" element={<Types />} />
            <Route path="masters/vehicles" element={<Vehicles />} />
            <Route path="masters/designations" element={<Designations />} />
            <Route path="tracking/voucher" element={<TrackByVoucher />} />
            <Route path="tracking/bill" element={<TrackByBill />} />
            <Route path="tracking/customer" element={<TrackByCustomer />} />
            <Route path="tracking/driver" element={<DriverTracking />} />
            <Route path="tracking/activity-tracker" element={<ActivityTrackerReport />} />
            <Route path="accounts/employees" element={<Employees />} />
            <Route path="accounts/addEmployee" element={<AddEmployee />} />
            <Route path="accounts/addEmployee/:employeeId" element={<AddEmployee />} />
            <Route path="accounts/clients" element={<Clients />} />
            <Route path="accounts/clients/:clientId/price-chart" element={<ClientPriceChart />} />
            <Route path="accounts/addClient" element={<AddClient />} />
            <Route path="accounts/addClient/:clientId" element={<AddClient />} />
            <Route path="accounts/customers" element={<Customers />} />
            <Route path="accounts/addCustomer" element={<AddCustomer />} />
            <Route path="accounts/addCustomer/:customerId" element={<AddCustomer />} />
            <Route path="accounts/agents" element={<Agents />} />
            <Route path="accounts/addAgent" element={<AddAgent />} />
            <Route path="accounts/addAgent/:agentId" element={<AddAgent />} />
            <Route path="accounts/users" element={<AccountUsers />} />
            <Route path="vehicle-management/vehicle-list" element={<VehicleList />} />
            <Route path="vehicle-management/add-edit-vehicle" element={<AddEditVehicle />} />
            <Route path="vehicle-management/add-edit-vehicle/:vehicleId" element={<AddEditVehicle />} />
            <Route path="vehicle-management/vehicle-details/:vehicleId" element={<VehicleDetails />} />
            <Route path="vehicle-management/maintenance" element={<VehicleMaintenance />} />
            <Route path="vehicle-management/maintenance/:vehicleId" element={<VehicleMaintenance />} />
            <Route path="vehicle-management/assignments" element={<VehicleAssignments />} />
            <Route path="vehicle-management/assignments/:vehicleId" element={<VehicleAssignments />} />
            <Route path="vehicle-management/documents" element={<VehicleDocuments />} />
            <Route path="vehicle-management/documents/:vehicleId" element={<VehicleDocuments />} />
            <Route path="vehicle-management/insurance" element={<VehicleInsurance />} />
            <Route path="vehicle-management/insurance/:vehicleId" element={<VehicleInsurance />} />
            <Route path="vehicle-management/fine-records" element={<VehicleFineRecords />} />
            <Route path="vehicle-management/fine-records/:vehicleId" element={<VehicleFineRecords />} />
            <Route path="vehicle-management/fuel-logs" element={<VehicleFuelLogs />} />
            <Route path="vehicle-management/fuel-logs/:vehicleId" element={<VehicleFuelLogs />} />
            <Route path="vehicle-management/permits" element={<VehiclePermits />} />
            <Route path="vehicle-management/permits/:vehicleId" element={<VehiclePermits />} />
            <Route path="vehicle-management/inspections" element={<VehicleInspections />} />
            <Route path="vehicle-management/inspections/:vehicleId" element={<VehicleInspections />} />
            <Route path="vehicle-management/vehicle-expenses" element={<VehicleExpenses />} />
            <Route path="vehicle-management/vehicle-expenses/:vehicleId" element={<VehicleExpenses />} />
            <Route path="vehicle-management/odometer-logs" element={<VehicleOdometerLogs />} />
            <Route path="vehicle-management/odometer-logs/:vehicleId" element={<VehicleOdometerLogs />} />
            <Route path="vehicle-management/accidents" element={<VehicleAccidents />} />
            <Route path="vehicle-management/accidents/:vehicleId" element={<VehicleAccidents />} />
            <Route path="vehicle-management/service-history" element={<VehicleServiceHistory />} />
            <Route path="vehicle-management/service-history/:vehicleId" element={<VehicleServiceHistory />} />
            <Route path="vehicle-management/add-maintenance" element={<AddMaintenance />} />
            <Route path="vehicle-management/add-maintenance/:vehicleId" element={<AddMaintenance />} />
            <Route path="vehicle-management/add-maintenance/:vehicleId/:maintenanceId" element={<AddMaintenance />} />
            <Route path="vehicle-management/add-documents" element={<AddDocuments />} />
            <Route path="vehicle-management/add-documents/:vehicleId" element={<AddDocuments />} />
            <Route path="vehicle-management/add-documents/:vehicleId/:documentId" element={<AddDocuments />} />
            <Route path="vehicle-management/add-insurance" element={<AddInsurance />} />
            <Route path="vehicle-management/add-insurance/:vehicleId" element={<AddInsurance />} />
            <Route path="vehicle-management/add-insurance/:vehicleId/:insuranceId" element={<AddInsurance />} />
            <Route path="vehicle-management/add-assignment" element={<AddAssignment />} />
            <Route path="vehicle-management/add-assignment/:vehicleId" element={<AddAssignment />} />
            <Route path="vehicle-management/add-assignment/:vehicleId/:assignmentId" element={<AddAssignment />} />
            <Route path="vehicle-management/add-fine" element={<AddFine />} />
            <Route path="vehicle-management/add-fine/:vehicleId" element={<AddFine />} />
            <Route path="vehicle-management/add-fine/:vehicleId/:fineId" element={<AddFine />} />
            <Route path="messages/draft-emails" element={<DraftEmails />} />
            <Route path="messages/draft-sms" element={<DraftSMS />} />
            <Route path="messages/delivery-note-sms" element={<DeliveryNoteSMS />} />
            <Route path="customization/driver-commission" element={<DriverCommission />} />
            <Route path="customization/client-area-charges" element={<ClientAreaCharges />} />
            <Route path="customization/voucher-book" element={<VoucherBook />} />
            <Route path="customization/sms-configuration" element={<SMSConfiguration />} />
            <Route path="customization/whatsapp-configuration" element={<WhatsappConfiguration />} />
            <Route path="customization/document-number" element={<DocumentNumber />} />
            <Route path="customization/status" element={<Status />} />
            <Route path="customization/user-page-role" element={<UserPageRole />} />
            <Route path="customization/print-header" element={<PrintHeaderPage />} />
            <Route path="customization/order-entry-tab" element={<OrderEntryTabSettings />} />
            <Route path="user/profile" element={<UserProfile />} />
            <Route path="user/company-profile" element={<CompanyProfile />} />
            <Route path="user/change-password" element={<ChangePassword />} />
            <Route
              path="license-info"
              element={
                canViewLicenseInfo(readUserInfo().Role) ? <LicenseInfo /> : <Forbidden />
              }
            />
            <Route path="account-suspended" element={
              getPortalBlockReason(readCachedPortal()) === 'ContractExpired'
                ? <ContractExpired />
                : <AccountSuspended />
            } />
            <Route path="uploads/order-upload" element={<OrderUpload />} />
            <Route path="uploads/location-upload" element={<LocationUpload />} />
            <Route path="uploads/client-upload" element={<ClientUpload />} />
            <Route path="approvals/shipment-approvals" element={<ShipmentApprovals />} />
            <Route path="approvals/payment-approvals" element={<PaymentApprovals />} />
            <Route path="approvals/return-approvals" element={<ReturnApprovals />} />
            <Route path="store/in-scan" element={<InScan />} />
            <Route path="store/out-scan" element={<OutScan />} />
            <Route path="store/delivery-scan" element={<DeliveryScan />} />
            <Route path="store/bulk-in-scan" element={<BulkInScan />} />
            <Route path="store/bulk-out-scan" element={<BulkOutScan />} />
            <Route path="store/pickup-scan" element={<PickupScan />} />
            <Route path="orders/order-list" element={<OrderList />} />
            <Route path="orders/order-calendar" element={<OrderCalendar />} />
            <Route path="orders/todays-orders" element={<TodaysOrders />} />
            <Route path="orders/create-order" element={<CreateOrder />} />
            <Route path="orders/create-order/:orderId" element={<CreateOrder />} />
            <Route path="orders/hold-orders" element={<HoldOrders />} />
            <Route path="orders/reverse-cancel" element={<ReverseCancel />} />
            <Route path="orders/batch-print" element={<BatchPrint />} />
            <Route path="orders/:orderId/history-comparison" element={<OrderHistoryComparison />} />
            <Route path="orders/:orderId" element={<OrderDetails />} />
            <Route path="followup/delivery-order" element={<DeliveryOrderFollowup />} />
            <Route path="followup/pickup-order" element={<PickupOrderFollowup />} />
            <Route path="followup/driver" element={<DriverFollowup />} />
            <Route path="followup/payment" element={<PaymentFollowup />} />
            <Route path="followup/return" element={<ReturnFollowup />} />
            <Route path="expenses/driver-expense" element={<DriverExpenses />} />
            <Route path="expenses/company-expense" element={<CompanyExpenses />} />
            <Route path="pickup/allocation" element={<PickupAllocation />} />
            <Route path="pickup/notes" element={<PickupNotes />} />
            <Route path="pickup/status" element={<PickupStatus />} />
            <Route path="pickup/statistics" element={<PickupStatistics />} />
            <Route path="pickup/exchange" element={<PickupExchange />} />
            <Route path="pickup/push-pickup" element={<PushPickup />} />
            <Route path="pickup/client-pickup-request" element={<ClientPickupRequest />} />
            <Route path="pickup/customer-pickup-request" element={<CustomerPickupRequest />} />
            <Route path="warehouse/dashboard" element={<WarehouseDashboard />} />
            <Route path="warehouse/master" element={<WarehouseMaster />} />
            <Route path="warehouse/goods-receiving" element={<GoodsReceiving />} />
            <Route path="warehouse/inventory-register" element={<InventoryRegister />} />
            <Route path="warehouse/shipment-sorting" element={<ShipmentSorting />} />
            <Route path="warehouse/pick-list" element={<PickList />} />
            <Route path="warehouse/loading-verification" element={<LoadingVerification />} />
            <Route path="warehouse/dispatch-manifest" element={<DispatchManifest />} />
            <Route path="warehouse/transfer" element={<WarehouseTransferPage />} />
            <Route path="warehouse/returns-receiving" element={<ReturnsReceiving />} />
            <Route path="warehouse/stock-adjustment" element={<StockAdjustment />} />
            <Route path="warehouse/reports" element={<WarehouseReports />} />
            <Route path="delivery/allocation" element={<DeliveryAllocation />} />
            <Route path="delivery/notes" element={<DeliveryNotes />} />
            <Route path="delivery/statistics" element={<DeliveryStatistics />} />
            <Route path="delivery/exchange" element={<DeliveryExchange />} />
            <Route path="delivery/push-order" element={<PushOrder />} />
            <Route path="shipments/new-shipment" element={<NewShipment />} />
            <Route path="shipments/shipment-list" element={<ShipmentList />} />
            <Route path="shipments/edit-shipment/:shipmentId" element={<ConvertShipment />} />
            <Route path="shipments/convert-shipment/:deliveryNoteId" element={<ConvertShipment />} />
            <Route path="payments/create-payment" element={<CreatePayment />} />
            <Route path="payments/client-payments" element={<ClientPayments />} />
            <Route path="payments/pending-payments" element={<PendingPayments />} />
            <Route path="payments/payment-status/:paymentId" element={<PaymentStatus />} />
            <Route path="returns/create-return" element={<CreateReturn />} />
            <Route path="returns/client-returns" element={<ClientReturns />} />
            <Route path="returns/pending-returns" element={<PendingReturns />} />
            <Route path="returns/return-status/:returnId" element={<ReturnStatus />} />
            <Route path="reports/order-report" element={<OrderReport />} />
            <Route path="reports/delivery-report" element={<DeliveryReport />} />
            <Route path="reports/client-orders" element={<ClientOrderReport />} />
            <Route path="reports/customer-orders" element={<CustomerOrderReport />} />
            <Route path="reports/employee-orders" element={<EmployeeOrderReport />} />
            <Route path="reports/employee-timeline" element={<EmployeeTimeline />} />
            <Route path="reports/driver-commission" element={<DriverCommissionReport />} />
            <Route path="reports/advance-orders" element={<AdvanceOrderReport />} />
            <Route path="reports/driver-expense" element={<DriverExpenseReport />} />
            <Route path="reports/agent-orders" element={<AgentOrderReport />} />
            <Route path="reports/pending-orders" element={<PendingOrderReport />} />
            <Route path="reports/shipment-expense-report" element={<ShipmentExpenseReport />} />
            <Route path="reports/vat-report" element={<VatReport />} />
            <Route path="reports/company-expense" element={<CompanyExpenseReport />} />
            <Route path="reports/income-expense" element={<IncomeExpenseReport />} />
            <Route path="reports/client-payment-report" element={<ClientPaymentReport />} />
            <Route path="reports/client-return-report" element={<ClientReturnReport />} />
            <Route path="reports/shipment-report" element={<ShipmentReport />} />
            <Route path="reports/inactive-clients" element={<InactiveClientsReport />} />
            <Route path="reports/client-pickup-request-report" element={<ClientPickupRequestReport />} />
            <Route path="reports/customer-pickup-request-report" element={<CustomerPickupRequestReport />} />
            <Route path="counters/counter-nos" element={<CounterNos />} />
            <Route path="counters/change-store-ref" element={<ChangeStoreRef />} />
            <Route path="counters/area-store-no-lock" element={<AreaStoreNoLock />} />
            <Route path="counters/update-store-ref-no" element={<UpdateStoreRefNo />} />
            <Route path="admin/order-edit" element={<AdminOrderEdit />} />
            <Route path="admin/order-payment-edit" element={<AdminOrderPaymentEdit />} />
            <Route path="admin/order-status-edit" element={<AdminOrderStatusEdit />} />
            <Route path="admin/order-date-edit" element={<AdminOrderDateEdit />} />
            <Route path="admin/order-location-edit" element={<AdminOrderLocationEdit />} />
            <Route path="admin/order-adjustment" element={<AdminOrderAdjustment />} />
            <Route path="accounting/account-types" element={<AccountType />} />
            <Route path="accounting/chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="accounting/vouchers/:slug/:voucherId" element={<AccountingVoucherForm />} />
            <Route path="accounting/vouchers/:slug" element={<AccountingVoucherList />} />
            <Route path="accounting/pdc/:mode" element={<PdcList />} />
            <Route path="accounting/general-ledger" element={<GeneralLedgerReport />} />
            <Route path="accounting/:reportKey" element={<FinancialStatementReport />} />
            {REPORT_SECTIONS.map((s) => (
              <Route
                key={`report-hub-${s.id}`}
                path={`reports/${s.id}`}
                element={<ReportCategoryHub sectionId={s.id} />}
              />
            ))}
            <Route path="reports/:reportKey" element={<ReportPlaceholder />} />
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </main>
      </div>

      {logoutConfirmOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-confirm-title"
        >
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="border-b border-gray-100 px-5 py-4">
              <h3 id="logout-confirm-title" className="text-lg font-semibold text-gray-900">
                Log out?
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                You will need to sign in again to use the portal.
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performLogout}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
      {paymentWarnPortal ? (
        <PaymentDueWarning
          portal={paymentWarnPortal}
          onClose={() => setPaymentWarnPortal(null)}
        />
      ) : null}
      {contractWarnPortal && !paymentWarnPortal ? (
        <ContractExpiringWarning
          portal={contractWarnPortal}
          onClose={() => setContractWarnPortal(null)}
        />
      ) : null}
      {licenseWarnPortal && !paymentWarnPortal && !contractWarnPortal ? (
        <LicenseExpiredWarning
          portal={licenseWarnPortal}
          onClose={() => setLicenseWarnPortal(null)}
        />
      ) : null}
    </div>
  );
}

export default Home;

